import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import {
  _AmenityPageTabType,
  FilesReturnType,
  PrecomputedAmenityData,
  ReservationListFiltersType,
} from "~/types";
import { INTENTS, loadCookiesData, resolveActorUserName } from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import { mergeAvailability } from "~/utils/amenity/amenityAvailability";
import { parseDayTime } from "~/utils/date";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import * as APITypes from "~/API";
import { S3ServiceLambda } from "~/services/s3";
import { apiClient, AmenityDetailsLoaderResult } from "~/services/apiClient";

dayjs.extend(utc);

export async function AmenityDetailsPageLoader({
  request,
  params,
}: LoaderFunctionArgs): Promise<AmenityDetailsPageLoaderType | Response> {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const { searchParams } = new URL(request.url);
  const tab = (searchParams.get("tab") ?? "details") as _AmenityPageTabType;

  if (tab !== "reservations") {
    searchParams.delete("status");
  }

  const reservationListFilters: ReservationListFiltersType = {
    status:
      (searchParams.get("status") as APITypes.AmenityReservationStatus) ||
      undefined,
  };

  const amenityID = params.amenityID || "";
  const propertyID = params.propertyID || "";

  const result = await apiClient.loader<AmenityDetailsLoaderResult>(
    "amenity-details",
    { id: amenityID, ownerID, ownerType, userID: user.uid },
  );

  if (!result.success || !result.data) {
    console.error("Failed to load amenity details:", result.error);
    throw new Error(result.error ?? "Failed to load amenity details");
  }

  const data = result.data;
  if (propertyID && data.propertyDetails.id !== propertyID) {
    return redirect(`/property/${propertyID}`);
  }

  const reservations = data.reservations.map((r) => ({
    timeFrame: {
      start: parseDayTime(r.startDate),
      end: parseDayTime(r.endDate),
    },
    tenantName: r.tenantName,
    createdAt: dayjs(r.createdAt),
    data: r.data,
  }));

  const formattedAmenityDetails = {
    amenityDetails: data.amenityDetails,
    propertyDetails: data.propertyDetails,
    buildingDetails: data.buildingDetails,
    reservations,
    accessRights: data.accessRights ?? undefined,
  };

  const amenityData: PrecomputedAmenityData = {
    formattedAmenityDetails,
    availability: mergeAvailability(formattedAmenityDetails),
  };

  const files = await S3ServiceLambda.list({
    entityType: "amenity",
    entityID: amenityID,
  });

  const userName = await resolveActorUserName(user, authAccessInfo);
  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  return {
    amenityData,
    files: files.data,
    ownerID,
    ownerType,
    userName,
    buildingList: data.buildingList,
    tab,
    filters: reservationListFilters,
    canDeleteAmenity: isAdminOrSuperAdmin || data.permissions.canDeleteAmenity,
    canEditAmenity: isAdminOrSuperAdmin || data.permissions.canEditAmenity,
    canReadAmenity: isAdminOrSuperAdmin || data.permissions.canReadAmenity,
    isAdminOrSuperAdmin,
  };
}

export type AmenityDetailsPageLoaderType = {
  amenityData: PrecomputedAmenityData;
  files: FilesReturnType;
  ownerID: string;
  ownerType: APITypes.OwnerType;
  userName: string;
  buildingList: APITypes.Building[];
  tab: _AmenityPageTabType;
  filters: ReservationListFiltersType;
  canDeleteAmenity: boolean;
  canEditAmenity: boolean;
  canReadAmenity: boolean;
  isAdminOrSuperAdmin: boolean;
};

export const AmenityDetailsPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const propertyID = params.propertyID || "";
  const amenityID = params.amenityID || "";

  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) {
    return redirect("/explore");
  }

  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) {
    return redirect("/explore");
  }

  const userName = await resolveActorUserName(user, authAccessInfo);

  const amenityCacheTargets = [
    { entity: "amenity" as const, entityID: amenityID },
  ];
  const mutateAmenity = <T,>(fn: () => Promise<T>) =>
    withEntityCacheInvalidation(amenityCacheTargets, fn);

  switch (intent) {
    case INTENTS._edit_amenity: {
      return mutateAmenity(async () => {
        const payload = JSON.parse(formData.get("body") as string) as Record<
          string,
          unknown
        >;
        const result = await apiClient.sendIntent<APITypes.Amenity>(
          INTENTS._edit_amenity,
          { amenityID, propertyID, ...payload },
        );
        if (result.success) {
          return { success: true, data: result.data ?? null };
        }
        return {
          success: false,
          data: null,
          error: result.error ?? "Failed to update amenity",
        };
      });
    }
    case INTENTS._edit_amenity_availability: {
      return mutateAmenity(async () => {
        const payload = JSON.parse(formData.get("body") as string) as Record<
          string,
          unknown
        >;
        const result = await apiClient.sendIntent<APITypes.Amenity>(
          INTENTS._edit_amenity_availability,
          { amenityID, propertyID, ...payload },
        );
        if (result.success) {
          return { success: true, data: result.data ?? null };
        }
        return {
          success: false,
          data: null,
          error: result.error ?? "Failed to update amenity availability",
        };
      });
    }
    case INTENTS._edit_amenity_reservation_rule: {
      return mutateAmenity(async () => {
        const payload = JSON.parse(formData.get("body") as string) as Record<
          string,
          unknown
        >;
        const result = await apiClient.sendIntent<APITypes.Amenity>(
          INTENTS._edit_amenity_reservation_rule,
          { amenityID, propertyID, ...payload },
        );
        if (result.success) {
          return { success: true, data: result.data ?? null };
        }
        return {
          success: false,
          data: null,
          error: result.error ?? "Failed to update amenity reservation rule",
        };
      });
    }
    case INTENTS._close_amenity: {
      return mutateAmenity(async () => {
        const payload = JSON.parse(formData.get("body") as string) as Record<
          string,
          unknown
        >;
        const result = await apiClient.sendIntent<APITypes.Amenity>(
          INTENTS._close_amenity,
          { amenityID, propertyID, ...payload },
        );
        if (result.success) {
          return { success: true, data: result.data ?? null };
        }
        return {
          success: false,
          data: null,
          error: result.error ?? "Failed to close amenity",
        };
      });
    }
    case INTENTS._reopen_amenity: {
      return mutateAmenity(async () => {
        const payload = JSON.parse(formData.get("body") as string) as Record<
          string,
          unknown
        >;
        const result = await apiClient.sendIntent<APITypes.Amenity>(
          INTENTS._reopen_amenity,
          { amenityID, propertyID, ...payload },
        );
        if (result.success) {
          return { success: true, data: result.data ?? null };
        }
        return {
          success: false,
          data: null,
          error: result.error ?? "Failed to reopen amenity",
        };
      });
    }
    case INTENTS._remove_amenity: {
      return mutateAmenity(async () => {
        const payload = JSON.parse(formData.get("body") as string) as Record<
          string,
          unknown
        >;
        const result = await apiClient.sendIntent<{
          deleted?: boolean;
          id?: string;
        }>(INTENTS._remove_amenity, { amenityID, propertyID, ...payload });
        if (result.success) {
          return redirect(`/property/${propertyID}`);
        }
        return {
          success: false,
          data: null,
          error: result.error ?? "Failed to remove amenity",
        };
      });
    }
    case INTENTS._delete_file: {
      const key: string = formData.get("body") as string;
      const result = await S3ServiceLambda.remove({ key });
      return result;
    }
    case INTENTS._edit_amenity_reservations_approve: {
      return mutateAmenity(async () => {
        const payload = JSON.parse(formData.get("body") as string) as {
          reservationIDs: string[];
          reservationVersions: Record<string, number>;
        };
        const result = await apiClient.sendIntent<APITypes.AmenityReservation[]>(
          INTENTS._edit_amenity_reservations_approve,
          {
            amenityID,
            propertyID,
            reservationIDs: payload.reservationIDs,
            reservationVersions: payload.reservationVersions,
            reviewer: userName,
          },
        );
        if (result.success) {
          return { success: true, data: result.data ?? [] };
        }
        return {
          success: false,
          data: null,
          error: result.error ?? "Failed to approve reservations",
        };
      });
    }
    case INTENTS._edit_amenity_reservations_deny: {
      return mutateAmenity(async () => {
        const payload = JSON.parse(formData.get("body") as string) as {
          reservationIDs: string[];
          reservationVersions: Record<string, number>;
          reason?: string;
        };
        const result = await apiClient.sendIntent<APITypes.AmenityReservation[]>(
          INTENTS._edit_amenity_reservations_deny,
          {
            amenityID,
            propertyID,
            reservationIDs: payload.reservationIDs,
            reservationVersions: payload.reservationVersions,
            reviewer: userName,
            reason: payload.reason || "",
          },
        );
        if (result.success) {
          return { success: true, data: result.data ?? [] };
        }
        return {
          success: false,
          data: null,
          error: result.error ?? "Failed to deny reservations",
        };
      });
    }
  }
  return null;
};
